// shared-lib/cabinet-runs/moveAppliances.js
//
// A run OWNS THE POSITION of the appliances in its bays (layout-relationships
// §2.5). After a fill, every appliance bay has a solved centre; this moves the
// appliance object — and everything sharing its fixture group: hood, surround,
// basin, faucet — ALONG THE WALL so the object's wall-param equals that centre.
// A range, fridge or dishwasher is also seated ACROSS the wall, back on the
// wall face (`BACK_TO_WALL_BAY_KINDS`) — that move is the appliance's alone.
// Objects are moved, never re-emitted: ids, models, customization and links
// all survive. A group moves rigidly here; the caller settles its links with
// `resolveGroup` afterwards.
//
// Pure. Returns the same array (and the same objects) when nothing moves.

const {
  worldToWallParam,
  getWallAxes,
  wallParamToSurfacePoint,
} = require('../kitchen-assembly/wallMath');
const {
  baysForRun,
  bayMatchesObject,
  isApplianceKind,
  isIncumbentSeatedKind,
  applianceSeatOf,
} = require('./bays');

const MOVE_EPS = 1e-3; // 1 mm along the wall: below this nothing is "moved"
const RUN_BAY_STEP = 'run-bay';

/** Signed distance of a point from a wall's face, along the wall's normal. */
function normalOffset(wall, point) {
  const axes = getWallAxes(wall);
  if (!axes || !point) return null;
  const p = wallParamToSurfacePoint(wall, worldToWallParam(wall, point) ?? 0);
  if (!p) return null;
  return (
    (point.x - p.x) * axes.wallNormal.x + (point.z - p.z) * axes.wallNormal.z
  );
}

/**
 * Bay kinds whose appliance is a BODY standing against the wall, so its back
 * belongs on the wall face — the seat the fill gives every cabinet beside it.
 * A product swap keeps the old centre and leaves the seat to the re-solve, so
 * without this a deeper range sank into the wall by half its extra depth
 * (2.27" on a 29.5" range that replaced a 25" one) and a shallower fridge
 * stood off it. Not a sink or a cooktop (`sink-base`, `cooktop`): those sit in
 * a base cabinet and keep their own setback. Not a tall unit, which the
 * incumbent passes seat.
 */
const BACK_TO_WALL_BAY_KINDS = new Set(['range', 'fridge', 'dishwasher']);

/**
 * How far `obj` must move along the wall's normal for its back to land on the
 * wall face, on the side of the wall it already stands on — a wall drawn the
 * other way round must not pull it through. `already` is a move along the
 * normal the caller makes anyway (a wall that moved). 0 when the object has
 * no depth to seat by.
 */
function backToWallShift(wall, obj, already = 0) {
  const depth = obj.dimensions?.z;
  const off = normalOffset(wall, obj.center);
  if (!(depth > 0) || off == null) return 0;
  const at = off + already;
  return (at < 0 ? -1 : 1) * (depth / 2) - at;
}

/** The object a bay holds: by `bayId` stamp, else by the bay's applianceId. */
// The APPLIANCE, never the cabinet under it: a sink-base cabinet the fill
// emits carries the bay's id too, and taking it as the anchor moved the box
// (which the fill re-emits anyway) and left the basin + faucet behind.
const CABINET_TYPES = new Set([
  'lower-cabinet',
  'upper-cabinet',
  'corner-cabinet',
]);
function applianceObjectFor(bay, objects) {
  const notCabinet = (o) => o && !CABINET_TYPES.has(o.type);
  // applianceId first (it names the true appliance, never a cabinet), then
  // the shared matcher (#1527) restricted to non-cabinets — the fill's
  // sink-base CABINET shares the bay's ids and must not win.
  return (
    (bay.applianceId ? objects.find((o) => o?.id === bay.applianceId) : null) ||
    objects.find((o) => notCabinet(o) && bayMatchesObject(bay, o)) ||
    null
  );
}

/**
 * Everything anchored to `anchor` — an accessory that computes its position
 * from it, and an accessory of that accessory (a faucet on its basin). Read
 * from `parents[]`, which names a specific host, never from the fixture
 * group, which only names a set.
 */
function accessoriesOf(anchor, objects) {
  const ids = new Set([anchor.id]);
  let grew = true;
  while (grew) {
    grew = false;
    for (const o of objects) {
      if (!o?.id || ids.has(o.id)) continue;
      if ((o.parents || []).some((p) => ids.has(p?.id))) {
        ids.add(o.id);
        grew = true;
      }
    }
  }
  ids.delete(anchor.id);
  return ids;
}

/**
 * Does `groupId` reach an appliance a DIFFERENT bay of this run anchors?
 *
 * A generated island stamps every piece it emits with one
 * `kitchenIsland_<stamp>` — cabinets, both appliances, the end panel, the
 * bar stools — so the flat "we move together" group spans the sink bay AND
 * the dishwasher bay. Carrying it wholesale would let a nudge of the sink
 * drag the dishwasher and the stools along the island, which is the very
 * tear the run ownership rules exist to prevent. A group that spans two bays
 * is not one installation, so the mover falls back to the anchor's own
 * accessories. A sink group (`kitchenSink_<id>` — cabinet, basin, faucet) or
 * an appliance group (`kitchenAppliance_<id>` — the range and its hood)
 * never spans two bays, so nothing on a wall run changes.
 */
function groupSpansAnotherBay(groupId, bay, run, objects) {
  if (!groupId) return false;
  for (const other of baysForRun(run)) {
    if (other.id === bay.id || !isApplianceKind(other.kind)) continue;
    const obj = applianceObjectFor(other, objects);
    if (obj?.fixtureGroupId && obj.fixtureGroupId === groupId) return true;
  }
  return false;
}

/**
 * @param {Object[]} objects — the room's objects (or any superset of the run's)
 * @param {Object} run — rehydrated run (segments carry `.wall`)
 * @param {Object} fillOut — `fillRun`'s return; `bays` is the solved table.
 *   `materializeRun` adds `freeSlidBays`: a free-standing leg's bays that a
 *   dishwasher's end piece slid, each carrying the leg's virtual `wall` —
 *   the wall its params are measured along, since a free segment has none
 * @param {Object} [ctx] — `{ now?, prevWallsById?, step? }`; `step` names the
 *   `scanAdjusted` step a scan run's moves are recorded under (default
 *   `run-bay`, the web re-solve)
 * @returns {{ objects: Object[], moved: string[] }}
 */
function moveApplianceObjectsToBays(objects, run, fillOut, ctx = {}) {
  const list = Array.isArray(objects) ? objects : [];
  const solved = [...(fillOut?.bays || []), ...(fillOut?.freeSlidBays || [])];
  if (!solved.length) return { objects: list, moved: [] };
  const bayById = new Map(baysForRun(run).map((b) => [b.id, b]));
  // Bays the fill could not place where they stand (`spanPacking.js` →
  // `fillRun` → here as part of the same `fillOut`), so no plumbing is needed.
  const blockedBayIds = new Set(
    (fillOut?.unsatisfied || []).map((u) => u?.bayId).filter(Boolean)
  );
  const moved = [];
  let next = list;

  for (const sb of solved) {
    const bay = bayById.get(sb.bayId);
    if (!bay || !isApplianceKind(bay.kind)) continue;
    // #2020: an incumbent pass HOLDS a measured body at its anchor when the
    // run reports its bay unsatisfied, and this loop runs after that pass.
    // Moving it now undoes the hold and contradicts the warning it emitted.
    // Only the unsatisfied case — a tall unit the run could place is seated
    // at the solved centre by both, so the ghost and the commit agree.
    if (isIncumbentSeatedKind(bay.kind) && blockedBayIds.has(sb.bayId))
      continue;
    const seg = run.segments?.[sb.segIndex];
    const wall = sb.wall || seg?.wall;
    const axes = wall ? getWallAxes(wall) : null;
    if (!axes) continue;
    const anchorObj = applianceObjectFor(bay, next);
    if (!anchorObj?.center) continue;
    const current = worldToWallParam(wall, anchorObj.center);
    if (current == null) continue;
    // WHERE THIS APPLIANCE BELONGS. The solved box centre, unless the bay
    // states a seat of its own — a vanity's sink bay does when its box is
    // wider than the bowl's base, or when the run is being kept as built, so
    // that a scanned bowl is not dragged to the middle of the cabinetry
    // around it (`vanitySinkBay.js`). That seat is held RELATIVE TO THE BAY:
    // where the fill stood the bay elsewhere than its anchor, the bowl goes
    // with its box (`applianceSeatOf`).
    const target = applianceSeatOf(bay, sb.center);
    const delta = target - current;
    // Across the wall: when the caller knows the wall as it WAS
    // (`ctx.prevWallsById`, a wall move), keep the object's offset from the
    // wall's face — a wall pushed out along its normal takes its sink and
    // dishwasher with it. Without it only the along-wall param was corrected
    // and a moved wall left its appliances standing in the room.
    const prev = ctx.prevWallsById?.get(wall.id) || null;
    let across = 0;
    let dTheta = 0; // the wall turned (a vertex move): the appliances turn with it
    if (prev) {
      const offBefore = normalOffset(prev, anchorObj.center);
      const offNow = normalOffset(wall, anchorObj.center);
      if (offBefore != null && offNow != null) across = offBefore - offNow;
      const prevAxes = getWallAxes(prev);
      if (prevAxes) dTheta = axes.wallRotationY - prevAxes.wallRotationY;
    }
    // And its own seat across the wall, for the appliance alone: see
    // `BACK_TO_WALL_BAY_KINDS`. The group keeps its own seats — a hood hangs
    // at its own depth from the wall, whatever the range below it measures.
    const seat = BACK_TO_WALL_BAY_KINDS.has(bay.kind)
      ? backToWallShift(wall, anchorObj, across)
      : 0;
    if (
      Math.abs(delta) <= MOVE_EPS &&
      Math.abs(across) <= MOVE_EPS &&
      Math.abs(seat) <= MOVE_EPS &&
      Math.abs(dTheta) <= 1e-6
    )
      continue;
    // WHAT TRAVELS WITH THIS APPLIANCE. Its own accessories always — a
    // basin's faucet names the basin in `parents[]` — and then the whole
    // fixture group, but ONLY where that group is one installation.
    //
    // `groupSpansAnotherBay` is the guard, and it was wired into the
    // free-standing path alone until 2026-09-27; the wall path dragged the
    // flat group unconditionally. A DOUBLE VANITY is exactly the case the
    // guard describes: both basins, both faucets, the cabinet, the counter,
    // the mirror and the light carry ONE `vanity_<id>`, so seating basin A
    // translated basin B with it — both bowls moved by A's delta and neither
    // landed in its own sink cabinet (measured 3.77" off on `D6B52714`, which
    // is what product reported). A group that spans two bays is not one
    // installation.
    const groupId = bay.fixtureGroupId || anchorObj.fixtureGroupId || null;
    const ids = new Set([anchorObj.id]);
    for (const id of accessoriesOf(anchorObj, next)) ids.add(id);
    if (groupId && !groupSpansAnotherBay(groupId, bay, run, next)) {
      for (const o of next) if (o?.fixtureGroupId === groupId) ids.add(o.id);
    }
    // And whatever the groups say, one bay's seat never carries another bay's
    // appliance — the same belt the free-standing path wears below.
    for (const other of baysForRun(run)) {
      if (other.id === bay.id || !isApplianceKind(other.kind)) continue;
      const obj = applianceObjectFor(other, next);
      if (!obj?.id) continue;
      ids.delete(obj.id);
      for (const id of accessoriesOf(obj, next)) ids.delete(id);
    }
    // Nor a piece this run BUILDS — the hood over a range, the sink base under
    // a sink. The solve already put it at the new seat, so carrying it moved
    // it twice and the save left it off its appliance by the drag's distance
    // (2026-10-17). The free-standing path's re-emission guard, below.
    for (const o of next) {
      if (o?.runId === run.id && o.id !== anchorObj.id) ids.delete(o.id);
    }
    const dx = delta * axes.wallDir.x + across * axes.wallNormal.x;
    const dz = delta * axes.wallDir.z + across * axes.wallNormal.z;
    const now = ctx.now || new Date().toISOString();
    // Group members turn about the anchor object's NEW centre, so a basin
    // and faucet keep their offsets from the sink after the wall turned.
    const pivot = {
      x: anchorObj.center.x + dx,
      z: anchorObj.center.z + dz,
    };
    const cos = Math.cos(dTheta);
    const sin = Math.sin(dTheta);
    // Only the appliance re-seated: the group has nowhere to go, so leave it
    // untouched rather than stamp it moved.
    const groupMoves = Math.hypot(dx, dz) > 1e-9 || Math.abs(dTheta) > 1e-6;
    next = next.map((o) => {
      if (!ids.has(o?.id) || !o.center) return o;
      if (o.id !== anchorObj.id && !groupMoves) return o;
      moved.push(o.id);
      let cx = o.center.x + dx;
      let cz = o.center.z + dz;
      if (Math.abs(dTheta) > 1e-6) {
        const rx = cx - pivot.x;
        const rz = cz - pivot.z;
        // Three.js Y rotation: +θ turns +x toward -z
        cx = pivot.x + rx * cos + rz * sin;
        cz = pivot.z - rx * sin + rz * cos;
      }
      if (o.id === anchorObj.id) {
        cx += seat * axes.wallNormal.x;
        cz += seat * axes.wallNormal.z;
      }
      const out = {
        ...o,
        center: { ...o.center, x: cx, z: cz },
        ...(Math.abs(dTheta) > 1e-6
          ? {
              rotation: {
                ...(o.rotation || {}),
                y: (o.rotation?.y || 0) + dTheta,
              },
            }
          : {}),
        lastModified: now,
      };
      if (run.source === 'scan') {
        out.scanAdjusted = [
          ...(Array.isArray(o.scanAdjusted) ? o.scanAdjusted : []),
          { step: ctx.step || RUN_BAY_STEP, kind: 'moved', deltaParam: delta },
        ];
      }
      return out;
    });
  }
  return { objects: next, moved };
}

/**
 * Translate the appliances a FREE-STANDING leg's bays anchor by the leg's own
 * delta — the island/peninsula counterpart of `moveApplianceObjectsToBays`.
 *
 * That function serves a free-standing segment only for the bays a
 * dishwasher's end piece slid (`freeSlidBays`, which carry the leg's virtual
 * wall): it seats each appliance at its SOLVED param along a wall, and a
 * rehydrated free-standing segment carries none. The rest of a leg's solved
 * table is never handed back (#1537 M15), so a leg MOVE cannot use it. The result
 * was that a leg move re-emitted the island's cabinets at the new position and
 * left its sink standing where it was — measured 2026-09-07 on a real scan:
 * 13 run objects moved 0.5 m, the sink and its faucet did not, and the ghost
 * redrew the sink at the old spot because the preview had already hidden it.
 *
 * A leg move needs no solve to answer this. It is a pure TRANSLATION, so every
 * body the leg carries moves by the same delta the segment did. That is
 * deliberately less than the mover M15 asks for — it does not re-seat an
 * appliance the fill re-packed, only carries one the leg took with it — and it
 * is exactly right for this gesture.
 *
 * **Objects the run RE-EMITS are excluded.** An appliance's fixture group
 * routinely contains run-owned cabinetry (on the corpus island the sink's
 * group holds the sink, its faucet AND the lower cabinet the fill builds under
 * it), and the fill already materialises those at the new position. Moving one
 * here as well would apply the delta twice.
 *
 * **`ctx.bayId` narrows it to ONE bay** — the island counterpart of a bay
 * SLIDE, where the leg stands still and one appliance travels along it. The
 * delta is then along the leg's own line (the caller projects it), and two
 * extra rules apply, both because a generated island stamps every piece it
 * emits with one `kitchenIsland_<stamp>`:
 *
 *   - an appliance ANOTHER bay of this run anchors never travels, and
 *   - nor does the flat fixture group, when it spans such a bay.
 *
 * Without them, nudging an island's sink dragged its dishwasher, its end
 * panel and its bar stools along the island — the same tear the run's
 * ownership rules exist to prevent. What always travels is the appliance
 * itself and whatever is ANCHORED to it through `parents[]` (a basin's
 * faucet), which is how a sink installation crosses a group boundary. A wall
 * run's groups (`kitchenSink_<id>`, `kitchenAppliance_<id>`) never span two
 * bays, so nothing there changes.
 *
 * @param {Object[]} objects — the room's objects (or any superset)
 * @param {Object} run — the run, stored or rehydrated (`baysForRun` takes both)
 * @param {number} segIndex — the free-standing segment that moved
 * @param {{x: number, z: number}} delta — its world translation
 * @param {Object} [ctx] — `{ now?, step?, bayId? }`
 * @returns {{ objects: Object[], moved: string[] }}
 */
function translateFreeSegmentAppliances(
  objects,
  run,
  segIndex,
  delta,
  ctx = {}
) {
  const list = Array.isArray(objects) ? objects : [];
  const dx = delta?.x || 0;
  const dz = delta?.z || 0;
  if (!(Math.hypot(dx, dz) > MOVE_EPS)) return { objects: list, moved: [] };
  // The named segment must actually be FREE-STANDING. A wall-backed segment's
  // appliances belong to `moveApplianceObjectsToBays`, which seats them at
  // their solved param; translating them here as well would move a body the
  // solve is about to place, and by a delta that means nothing on a wall.
  // `run` must therefore be the STORED run — `segmentIndex` on a bay is a
  // stored index, and rehydrate FILTERS orphaned segments, so the two
  // numbering schemes part company on a run that has one.
  if (!(run?.segments || [])[segIndex]?.freestanding) {
    return { objects: list, moved: [] };
  }

  const oneBay = ctx.bayId || null;
  const ids = new Set();
  for (const bay of baysForRun(run)) {
    if ((bay.segmentIndex ?? 0) !== segIndex) continue;
    if (!isApplianceKind(bay.kind)) continue;
    if (oneBay && bay.id !== oneBay) continue;
    // The APPLIANCE, not the cabinet under it — the same reason
    // `applianceObjectFor` exists for the wall path.
    const anchorObj = applianceObjectFor(bay, list);
    if (!anchorObj?.center) continue;
    ids.add(anchorObj.id);
    // Anchored to it, hence carried whatever the groups say: a basin's faucet
    // names the basin in `parents[]`.
    if (oneBay) for (const id of accessoriesOf(anchorObj, list)) ids.add(id);
    const groupId = bay.fixtureGroupId || anchorObj.fixtureGroupId || null;
    if (!groupId) continue;
    if (oneBay && groupSpansAnotherBay(groupId, bay, run, list)) continue;
    for (const o of list) if (o?.fixtureGroupId === groupId) ids.add(o.id);
  }
  if (!ids.size) return { objects: list, moved: [] };
  // One bay's slide must never carry another bay's appliance, however the
  // groups are stamped.
  if (oneBay) {
    for (const other of baysForRun(run)) {
      if (other.id === oneBay || !isApplianceKind(other.kind)) continue;
      const obj = applianceObjectFor(other, list);
      if (!obj?.id) continue;
      ids.delete(obj.id);
      for (const id of accessoriesOf(obj, list)) ids.delete(id);
    }
  }

  const now = ctx.now || new Date().toISOString();
  const moved = [];
  const next = list.map((o) => {
    // `o.runId === run.id` is the re-emission guard above, not a style choice.
    if (!ids.has(o?.id) || !o.center || o.runId === run.id) return o;
    moved.push(o.id);
    const out = {
      ...o,
      center: { ...o.center, x: o.center.x + dx, z: o.center.z + dz },
      lastModified: now,
    };
    if (run.source === 'scan') {
      out.scanAdjusted = [
        ...(Array.isArray(o.scanAdjusted) ? o.scanAdjusted : []),
        { step: ctx.step || RUN_BAY_STEP, kind: 'moved' },
      ];
    }
    return out;
  });
  return { objects: next, moved };
}

module.exports = {
  moveApplianceObjectsToBays,
  translateFreeSegmentAppliances,
  applianceObjectFor,
  accessoriesOf,
  MOVE_EPS,
};
